import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Plus, Trash2, Edit2, ChevronLeft, ChevronRight, Star, StarOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { celebrateMany } from '../lib/celebrate';
import { PieChart, Pie, Cell, ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from 'recharts';

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
// Initials: D-L-M-X-J-V-S (X for miércoles to avoid M-M ambiguity). Visually clear and unambiguous.
const WEEK_INIT = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];
const WEEK_ABBR = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

// Day-of-week picker (icon-style toggles like the calendar image)
const DayPicker = ({ value, onChange, theme, label }) => {
  const order = [1, 2, 3, 4, 5, 6, 0]; // Lun..Sáb, Dom
  const toggle = (d) => {
    const has = value.includes(d);
    onChange(has ? value.filter(x => x !== d) : [...value, d]);
  };
  const allActive = order.every(d => value.includes(d));
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs uppercase tracking-wider" style={{ color: theme.textMuted }}>{label}</label>
        <button type="button" onClick={() => onChange(allActive ? [1, 2, 3, 4, 5] : [0, 1, 2, 3, 4, 5, 6])}
          className="text-[10px]" style={{ color: theme.accent }}>
          {allActive ? 'Solo días de semana' : 'Todos los días'}
        </button>
      </div>
      <div className="flex gap-1.5 flex-wrap">
        {order.map(d => {
          const active = value.includes(d);
          return (
            <button type="button" key={d} onClick={() => toggle(d)}
              className="w-11 h-11 rounded-lg flex flex-col items-center justify-center transition-all hover:scale-105"
              style={{
                background: active ? theme.primary : theme.elevated,
                border: `1.5px solid ${active ? theme.primary : theme.border}`,
                color: active ? '#fff' : theme.textMuted,
                boxShadow: active ? `0 4px 12px ${theme.primary}40` : 'none',
              }}>
              <span className="text-[9px] uppercase tracking-wider opacity-80">{WEEK_ABBR[d].slice(0, 3)}</span>
              <span className="text-[10px] font-bold">{WEEK_INIT[d]}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const Habits = () => {
  const [habits, setHabits] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [monthOffset, setMonthOffset] = useState(0);
  const [habitName, setHabitName] = useState('');
  const [habitFreq, setHabitFreq] = useState('daily');
  const [habitDays, setHabitDays] = useState([1, 2, 3, 4, 5, 6, 0]); // 0=Sun, 1=Mon... ALL by default
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editFreq, setEditFreq] = useState('daily');
  const [editDays, setEditDays] = useState([1, 2, 3, 4, 5, 6, 0]);
  const [editShow, setEditShow] = useState(true);
  // Health tracker local state with medications (named by user)
  const [health, setHealth] = useState({ sleep: {}, mood: {}, water: {}, meds: {} });
  const [medNames, setMedNames] = useState([]);
  const [newMedName, setNewMedName] = useState('');
  const { theme } = useTheme();
  const { t } = useLanguage();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchHabits(); loadHealth(); }, []);
  const fetchHabits = async () => { try { setHabits((await api.get('/habits')).data); } catch (err) { console.debug(err); } };
  const loadHealth = () => {
    try {
      const h = JSON.parse(localStorage.getItem('teko-health') || '{"sleep":{},"mood":{},"water":{},"meds":{}}');
      if (!h.meds) h.meds = {};
      setHealth(h);
      setMedNames(JSON.parse(localStorage.getItem('teko-meds') || '[]'));
    } catch (err) { console.debug(err); }
  };
  const saveHealth = (next) => { setHealth(next); localStorage.setItem('teko-health', JSON.stringify(next)); };
  const saveMeds = (next) => { setMedNames(next); localStorage.setItem('teko-meds', JSON.stringify(next)); };
  const addMed = () => {
    const n = newMedName.trim(); if (!n) return;
    saveMeds([...medNames, n]); setNewMedName('');
  };
  const removeMed = (name) => {
    saveMeds(medNames.filter(m => m !== name));
    const newMeds = { ...(health.meds || {}) }; delete newMeds[name]; saveHealth({ ...health, meds: newMeds });
  };
  const toggleMed = (medName, dateStr) => {
    const meds = { ...(health.meds || {}) };
    if (!meds[medName]) meds[medName] = {};
    meds[medName] = { ...meds[medName] };
    meds[medName][dateStr] = !meds[medName][dateStr];
    saveHealth({ ...health, meds });
  };

  const today = new Date();
  const viewDate = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
  const year = viewDate.getFullYear();
  const monthIdx = viewDate.getMonth();
  const daysInMonth = new Date(year, monthIdx + 1, 0).getDate();
  const days = Array.from({ length: daysInMonth }, (_, i) => {
    const d = new Date(year, monthIdx, i + 1);
    return { num: i + 1, date: d, str: d.toISOString().split('T')[0], dow: d.getDay() };
  });
  const todayStr = today.toISOString().split('T')[0];

  const addHabit = async () => {
    if (!habitName.trim()) return;
    try {
      const days = habitDays.map(String);
      await api.post('/habits', { name: habitName, frequency: habitFreq, custom_days: days, show_in_dashboard: true });
      toast.success('Agregado');
      setHabitName(''); setHabitFreq('daily'); setHabitDays([1, 2, 3, 4, 5, 6, 0]); setShowAdd(false); fetchHabits();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const updateHabit = async () => {
    if (!editName.trim() || !editId) return;
    try {
      await api.put(`/habits/${editId}`, { name: editName, frequency: editFreq, custom_days: editDays.map(String), show_in_dashboard: editShow });
      toast.success('Actualizado'); setShowEdit(false); setEditId(null); fetchHabits();
    } catch { toast.error('Error'); }
  };

  const toggleDashboard = async (h) => {
    try {
      await api.put(`/habits/${h.id}`, { show_in_dashboard: !(h.show_in_dashboard !== false) });
      fetchHabits();
    } catch (err) { console.debug(err); }
  };

  const deleteHabit = async (id) => {
    try { await api.delete(`/habits/${id}`); toast.success('Eliminado'); fetchHabits(); } catch (err) { console.debug(err); }
  };

  const toggleDay = async (habitId, dateStr) => {
    const h = habits.find(x => x.id === habitId);
    if (!h) return;
    try {
      if (h.check_ins?.includes(dateStr)) {
        await api.post(`/habits/${habitId}/uncheck`, { date: dateStr });
      } else {
        const { data } = await api.post(`/habits/${habitId}/check-in`, { date: dateStr });
        if (data?.new_achievements?.length) celebrateMany(data.new_achievements);
      }
      fetchHabits();
    } catch (err) { console.debug(err); }
  };

  const openEdit = (h) => { 
    setEditId(h.id); setEditName(h.name); setEditFreq(h.frequency); setEditShow(h.show_in_dashboard !== false);
    const days = (h.custom_days && h.custom_days.length > 0) ? h.custom_days.map(d => parseInt(d)).filter(d => !isNaN(d)) : [0,1,2,3,4,5,6];
    setEditDays(days);
    setShowEdit(true);
  };

  // Returns true if the habit applies on a given weekday (0=Sun..6=Sat).
  // - frequency='daily' → applies every day
  // - frequency='weekly' → applies only if dow ∈ custom_days
  //   `custom_days` may be stored as numbers (0-6) or as letter codes ['L','M','X','J','V','S','D'].
  const habitAppliesOnDow = (h, dow) => {
    const freq = h.frequency || 'daily';
    if (freq === 'daily') return true;
    if (freq === 'weekly') {
      const days = h.custom_days || [];
      if (days.length === 0) return true; // legacy weekly with no day list → treat as every day
      const dowNumStr = String(dow);
      const dowLetter = ['D', 'L', 'M', 'X', 'J', 'V', 'S'][dow];
      return days.some((d) => String(d) === dowNumStr || d === dowLetter);
    }
    return true;
  };

  // Overall monthly progress (only count slots that actually apply on each day).
  let totalSlots = 0;
  let filledSlots = 0;
  habits.forEach((h) => {
    days.forEach((d) => {
      if (habitAppliesOnDow(h, d.dow)) {
        totalSlots += 1;
        if (h.check_ins?.includes(d.str)) filledSlots += 1;
      }
    });
  });
  const overallPct = totalSlots > 0 ? Math.round((filledSlots / totalSlots) * 100) : 0;

  // Per-day completion % — count only habits that apply on that specific day.
  const dailyPct = days.map((d) => {
    const applicable = habits.filter((h) => habitAppliesOnDow(h, d.dow));
    if (applicable.length === 0) return 0;
    const done = applicable.filter((h) => h.check_ins?.includes(d.str)).length;
    return Math.round((done / applicable.length) * 100);
  });

  // Monthly summary: pct per day (for area chart)
  const lineData = days.map((d, i) => ({ day: d.num, pct: dailyPct[i] }));

  const inputStyle = { background: theme.elevated, borderColor: theme.border };

  // Bar gradient color mapping — early months -> peach -> pink -> purple
  // Returns black or white text depending on background luminance
  const pickTextColor = (hex) => {
    if (!hex || typeof hex !== 'string') return theme.textPrimary;
    let h = hex.replace('#', '');
    if (h.length === 3) h = h.split('').map(c => c + c).join('');
    if (h.length !== 6) return theme.textPrimary;
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return lum > 0.55 ? '#0A0606' : '#FFFFFF';
  };

  const getBarColor = (dayIdx) => {
    const t = dayIdx / daysInMonth;
    if (t < 0.33) return theme.light || theme.accent;
    if (t < 0.66) return theme.accent;
    return theme.primary;
  };

  // Cell checked color — same gradient by column position
  const getCellColor = (dayIdx) => getBarColor(dayIdx);

  return (
    <div data-testid="habits-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-4xl sm:text-5xl font-light tracking-tight mb-1" style={{ color: theme.accent }}>{t('habits')}</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center p-2 rounded-lg" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
            <button onClick={() => setMonthOffset(monthOffset - 1)} className="p-1" style={{ color: theme.accent }}><ChevronLeft size={18} /></button>
            <span translate="no" className="text-sm font-medium mx-3" style={{ color: theme.accent }}>{MONTHS[monthIdx]} {year}</span>
            <button onClick={() => setMonthOffset(monthOffset + 1)} className="p-1" style={{ color: theme.accent }}><ChevronRight size={18} /></button>
          </div>
          <Dialog open={showAdd} onOpenChange={setShowAdd}>
            <DialogTrigger asChild>
              <Button data-testid="add-habit-btn" className="text-white" style={{ background: theme.primary }}><Plus size={16} className="mr-1" />Hábito</Button>
            </DialogTrigger>
            <DialogContent style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
              <DialogHeader><DialogTitle style={{ color: theme.accent }}>Agregar Hábito</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <Input placeholder="Nombre" value={habitName} onChange={(e) => setHabitName(e.target.value)} className="text-white" style={inputStyle} />
                <Select value={habitFreq} onValueChange={setHabitFreq}>
                  <SelectTrigger className="text-white" style={inputStyle}><SelectValue /></SelectTrigger>
                  <SelectContent style={{ background: theme.card, borderColor: theme.border }}>
                    <SelectItem value="daily">Diario</SelectItem>
                    <SelectItem value="weekly">Semanal</SelectItem>
                  </SelectContent>
                </Select>
                <DayPicker value={habitDays} onChange={setHabitDays} theme={theme} label="Días activos" />
                <Button onClick={addHabit} className="w-full text-white" style={{ background: theme.primary }}>Agregar</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
          <DialogHeader><DialogTitle style={{ color: theme.accent }}>Editar Hábito</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Input placeholder="Nombre" value={editName} onChange={(e) => setEditName(e.target.value)} className="text-white" style={inputStyle} />
            <Select value={editFreq} onValueChange={setEditFreq}>
              <SelectTrigger className="text-white" style={inputStyle}><SelectValue /></SelectTrigger>
              <SelectContent style={{ background: theme.card, borderColor: theme.border }}>
                <SelectItem value="daily">Diario</SelectItem>
                <SelectItem value="weekly">Semanal</SelectItem>
              </SelectContent>
            </Select>
            <DayPicker value={editDays} onChange={setEditDays} theme={theme} label="Días activos" />
            <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: theme.textSecondary }}>
              <input type="checkbox" checked={editShow} onChange={(e) => setEditShow(e.target.checked)} className="w-4 h-4" />
              Mostrar en Dashboard
            </label>
            <Button onClick={updateHabit} className="w-full text-white" style={{ background: theme.primary }}>Guardar</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ==== HABIT TRACKER MAIN PANEL (image 1 style) ==== */}
      <div className="rounded-lg p-5" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold uppercase tracking-widest" style={{ color: theme.accent }}>Habit Tracker</h2>
          <p translate="no" className="text-xs uppercase tracking-wider" style={{ color: theme.textMuted }}>{MONTHS[monthIdx]} {year}</p>
        </div>

        <div className="overflow-x-auto">
          <div style={{ minWidth: `${200 + daysInMonth * 28 + 60}px` }}>
            {/* Row 1: Donut (left) + Daily bars (right) */}
            <div className="flex items-end gap-3 mb-2">
              {/* Donut */}
              <div className="w-[200px] h-[120px] flex items-center justify-center shrink-0">
                <div className="relative w-24 h-24">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={[{ v: overallPct }, { v: 100 - overallPct }]} dataKey="v" innerRadius={32} outerRadius={46} startAngle={90} endAngle={-270}>
                        <Cell fill={theme.primary} />
                        <Cell fill={theme.elevated} />
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-lg font-bold" style={{ color: theme.accent }}>{overallPct}%</span>
                  </div>
                </div>
              </div>

              {/* Percentage labels + vertical bars */}
              <div className="flex-1 flex gap-0">
                {days.map((d, i) => (
                  <div key={i} className="text-center" style={{ width: '28px' }}>
                    <div className="text-[8px]" style={{ color: theme.textMuted }}>{dailyPct[i]}%</div>
                    <div className="mx-auto my-0.5 rounded-sm" style={{
                      width: '18px',
                      height: `${Math.max(4, (dailyPct[i] / 100) * 60)}px`,
                      background: getBarColor(i),
                      opacity: 0.85,
                    }} />
                  </div>
                ))}
              </div>
              <div className="w-[60px] shrink-0"></div>
            </div>

            {/* Row 2: Day-of-week abbreviations */}
            <div className="flex items-center gap-0 mb-0.5 pl-[200px]">
              {days.map((d, i) => (
                <div key={i} className="text-center" style={{ width: '28px' }}>
                  <div className="text-[9px]" style={{ color: theme.textMuted }}>{WEEK_ABBR[d.dow]}</div>
                </div>
              ))}
              <div className="w-[60px] shrink-0 text-center">
                <div className="text-[9px] font-semibold" style={{ color: theme.textMuted, writingMode: 'vertical-rl' }}>Goal / Actual</div>
              </div>
            </div>

            {/* Row 3: Day numbers (colored header band) */}
            <div className="flex items-center gap-0 mb-1 pl-[200px]">
              {days.map((d, i) => {
                const isToday = d.str === todayStr;
                return (
                  <div key={i} className="text-center py-1" style={{ width: '28px', background: getBarColor(i), opacity: isToday ? 1 : 0.7, borderRadius: i === 0 ? '6px 0 0 6px' : i === daysInMonth - 1 ? '0 6px 6px 0' : 0 }}>
                    <div className="text-[10px] font-bold" style={{ color: pickTextColor(getBarColor(i)) }}>{d.num}</div>
                  </div>
                );
              })}
            </div>

            {/* Row 4+: DAILY HABITS header + rows */}
            <div className="flex items-center pl-0 mt-3 mb-1" style={{ borderBottom: `2px solid ${theme.primary}` }}>
              <div className="w-[200px] pl-2 pb-1">
                <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: theme.accent }}>Daily Habits</span>
              </div>
              <div className="flex-1"></div>
              <div className="w-[60px] text-center pb-1">
                <span className="text-[9px] font-semibold" style={{ color: theme.textMuted }}>Total</span>
              </div>
            </div>

            {habits.length === 0 && <p className="text-center py-10 text-sm" style={{ color: theme.textMuted }}>Agrega tu primer hábito arriba</p>}

            {habits.map((habit, hIdx) => {
              const done = days.filter(d => habit.check_ins?.includes(d.str)).length;
              return (
                <div key={habit.id} className="flex items-center" style={{ height: '30px' }}>
                  <div className="w-[200px] pl-2 flex items-center gap-1.5">
                    <button onClick={() => toggleDashboard(habit)} title="Mostrar en Dashboard">
                      {habit.show_in_dashboard !== false
                        ? <Star size={11} style={{ color: theme.accent }} fill={theme.accent} />
                        : <StarOff size={11} style={{ color: theme.textMuted }} />}
                    </button>
                    <span className="text-xs truncate flex-1" style={{ color: theme.textPrimary }}>{habit.name}</span>
                    <button onClick={() => openEdit(habit)} className="opacity-60 hover:opacity-100" style={{ color: theme.textMuted }}><Edit2 size={10} /></button>
                    <button onClick={() => deleteHabit(habit.id)} className="opacity-60 hover:opacity-100" style={{ color: theme.textMuted }}><Trash2 size={10} /></button>
                  </div>
                  <div className="flex-1 flex gap-0">
                    {days.map((d, i) => {
                      const checked = habit.check_ins?.includes(d.str);
                      const cellColor = getCellColor(i);
                      const activeDays = (habit.custom_days && habit.custom_days.length > 0) ? habit.custom_days.map(x => parseInt(x)) : [0, 1, 2, 3, 4, 5, 6];
                      const isActive = activeDays.includes(d.dow);
                      return (
                        <div key={i} className="flex items-center justify-center" style={{ width: '28px', height: '30px' }}>
                          {isActive ? (
                            <button onClick={() => toggleDay(habit.id, d.str)}
                              className="w-[18px] h-[18px] rounded-sm flex items-center justify-center transition-all hover:scale-110"
                              style={{
                                background: checked ? cellColor : 'transparent',
                                border: `1.5px solid ${cellColor}`,
                                opacity: checked ? 1 : 0.6,
                              }}>
                              {checked && <span className="text-[11px] font-bold" style={{ color: pickTextColor(cellColor) }}>✓</span>}
                            </button>
                          ) : (
                            <div className="w-[18px] h-[18px] rounded-sm flex items-center justify-center"
                              style={{ background: 'transparent', border: `1px dashed ${theme.border}`, opacity: 0.3 }} title="Día inactivo">
                              <span className="text-[10px]" style={{ color: theme.textMuted }}>·</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="w-[60px] text-center">
                    <span className="text-xs font-semibold" style={{ color: theme.accent }}>{done}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ==== INDIVIDUAL HABIT STATS ==== */}
      {habits.length > 0 && (
        <div className="rounded-lg p-5" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
          <h2 className="text-sm font-semibold uppercase tracking-widest mb-4" style={{ color: theme.accent }}>Estadísticas por Hábito</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {habits.map((habit, hi) => {
              const allChecks = (habit.check_ins || []);
              const totalEver = allChecks.length;
              const monthDone = days.filter(d => allChecks.includes(d.str)).length;
              const monthPct = Math.round((monthDone / daysInMonth) * 100);
              // Current streak: consecutive days ending today (or yesterday if today not checked)
              let streak = 0;
              const todayObj = new Date();
              for (let i = 0; i < 365; i++) {
                const d = new Date(todayObj); d.setDate(todayObj.getDate() - i);
                const ds = d.toISOString().split('T')[0];
                if (allChecks.includes(ds)) streak++;
                else if (i === 0) continue; // allow today to not be done yet
                else break;
              }
              // Best streak ever
              let best = 0, run = 0;
              const sorted = [...allChecks].sort();
              for (let i = 0; i < sorted.length; i++) {
                if (i === 0) { run = 1; }
                else {
                  const prev = new Date(sorted[i - 1]);
                  const curr = new Date(sorted[i]);
                  if ((curr - prev) / 86400000 === 1) run++;
                  else run = 1;
                }
                if (run > best) best = run;
              }
              const color = getBarColor(hi);
              return (
                <div key={habit.id} className="rounded-lg p-3" style={{ background: theme.elevated, borderLeft: `3px solid ${color}` }}>
                  <p className="text-sm font-semibold truncate mb-2" style={{ color: theme.textPrimary }}>{habit.name}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-md p-2" style={{ background: `${color}15` }}>
                      <p className="text-[8px] uppercase tracking-wider" style={{ color: theme.textMuted }}>Mes actual</p>
                      <p className="text-base font-bold" style={{ color }}>{monthDone}<span className="text-[10px] opacity-60">/{daysInMonth}</span></p>
                      <p className="text-[9px]" style={{ color: theme.textMuted }}>{monthPct}%</p>
                    </div>
                    <div className="rounded-md p-2" style={{ background: `${theme.warning}15` }}>
                      <p className="text-[8px] uppercase tracking-wider" style={{ color: theme.textMuted }}>Racha</p>
                      <p className="text-base font-bold flex items-center gap-1" style={{ color: theme.warning }}>
                        🔥 {streak}
                      </p>
                      <p className="text-[9px]" style={{ color: theme.textMuted }}>Mejor: {best}d</p>
                    </div>
                    <div className="col-span-2 rounded-md p-2" style={{ background: `${theme.success}10` }}>
                      <p className="text-[8px] uppercase tracking-wider" style={{ color: theme.textMuted }}>Total histórico</p>
                      <p className="text-sm font-semibold" style={{ color: theme.success }}>{totalEver} check-ins</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ==== MONTHLY SUMMARY (area chart) ==== */}
      <div className="rounded-lg p-5" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
        <h2 className="text-sm font-semibold uppercase tracking-widest mb-3 text-center" style={{ color: theme.accent }}>Monthly Summary</h2>
        <ResponsiveContainer width="100%" height={140}>
          <AreaChart data={lineData}>
            <defs>
              <linearGradient id="habitGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={theme.primary} stopOpacity={0.6} />
                <stop offset="100%" stopColor={theme.primary} stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <XAxis dataKey="day" stroke={theme.textMuted} style={{ fontSize: '9px' }} />
            <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} stroke={theme.textMuted} style={{ fontSize: '9px' }} tickFormatter={(v) => `${v}%`} />
            <Tooltip contentStyle={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, borderRadius: '8px', fontSize: '11px' }} formatter={(v) => `${v}%`} />
            <Area type="monotone" dataKey="pct" stroke={theme.accent} strokeWidth={2} fill="url(#habitGradient)" dot={{ r: 2, fill: theme.accent }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* ==== HEALTH TRACKER ==== */}
      <div className="rounded-2xl overflow-hidden relative" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl pointer-events-none opacity-15" style={{ background: theme.primary }} />
        <div className="relative px-6 py-5 flex items-center justify-between flex-wrap gap-3" style={{ borderBottom: `1px solid ${theme.border}`, background: `linear-gradient(90deg, ${theme.primary}10, transparent 70%)` }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${theme.primary}25`, border: `1px solid ${theme.primary}55` }}>
              <span className="text-lg">🌿</span>
            </div>
            <div>
              <h2 className="text-base font-semibold tracking-tight" style={{ color: theme.textPrimary }}>Bienestar</h2>
              <p className="text-[11px]" style={{ color: theme.textMuted }}>Sueño · ánimo · hidratación · medicamentos</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Input value={newMedName} onChange={(e) => setNewMedName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addMed()}
              placeholder="Nombre del medicamento" className="h-9 text-xs w-48" style={inputStyle} />
            <Button onClick={addMed} size="sm" className="h-9 text-xs text-white" style={{ background: theme.primary }}>
              <Plus size={12} className="mr-1" />Medicamento
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto themed-scroll p-5" style={{ '--scroll-thumb': `${theme.primary}55`, '--scroll-track': `${theme.primary}15` }}>
          <div style={{ minWidth: `${220 + daysInMonth * 30}px` }}>
            {/* Date header */}
            <div className="flex items-center mb-3 pb-2" style={{ borderBottom: `1px solid ${theme.border}` }}>
              <div className="w-[200px] pl-2 text-[10px] uppercase tracking-[0.2em]" style={{ color: theme.textMuted }}>Métrica</div>
              <div className="flex-1 flex">
                {days.map((d, i) => {
                  const isToday = d.str === todayStr;
                  return (
                    <div key={i} className="text-center" style={{ width: '30px' }}>
                      <div className="text-[8px] uppercase" style={{ color: theme.textMuted, opacity: 0.7 }}>{WEEK_INIT[d.dow]}</div>
                      <div className={`text-[10px] font-semibold mt-0.5 ${isToday ? 'inline-flex items-center justify-center w-5 h-5 rounded-full' : ''}`}
                        style={{ color: isToday ? '#fff' : theme.textSecondary, background: isToday ? theme.primary : 'transparent' }}>
                        {d.num}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Numeric rows with emoji icons */}
            <HealthNumRow label="Sueño" emoji="🌙" unit="h" max={12} dayList={days} values={health.sleep}
              onChange={(str, v) => saveHealth({ ...health, sleep: { ...health.sleep, [str]: v } })} theme={theme} getColor={getBarColor} />
            <HealthNumRow label="Ánimo" emoji="✨" unit="" max={5} hint="1-5" dayList={days} values={health.mood}
              onChange={(str, v) => saveHealth({ ...health, mood: { ...health.mood, [str]: v } })} theme={theme} getColor={getBarColor} />
            <HealthNumRow label="Agua" emoji="💧" unit="vasos" max={20} dayList={days} values={health.water}
              onChange={(str, v) => saveHealth({ ...health, water: { ...health.water, [str]: v } })} theme={theme} getColor={getBarColor} />

            {/* Medication rows — checkboxes */}
            {medNames.length > 0 && (
              <div className="mt-4 pt-3" style={{ borderTop: `1px dashed ${theme.border}` }}>
                <div className="flex items-center gap-2 mb-2 pl-2">
                  <span className="text-base">💊</span>
                  <p className="text-[10px] uppercase tracking-[0.2em]" style={{ color: theme.accent }}>Medicamentos</p>
                </div>
                {medNames.map(med => {
                  const takenCount = days.filter(d => health.meds?.[med]?.[d.str]).length;
                  return (
                    <div key={med} className="flex items-center group rounded-md transition-colors hover:bg-white/[0.02]" style={{ height: '38px' }}>
                      <div className="w-[200px] pl-2 flex items-center gap-2">
                        <span className="text-xs truncate flex-1" style={{ color: theme.textPrimary }}>{med}</span>
                        <span className="text-[9px] px-1.5 py-0.5 rounded tabular-nums" style={{ background: `${theme.accent}25`, color: theme.accent }}>{takenCount}/{daysInMonth}</span>
                        <button onClick={() => removeMed(med)} className="opacity-0 group-hover:opacity-100" style={{ color: theme.error }}>
                          <Trash2 size={11} />
                        </button>
                      </div>
                      <div className="flex-1 flex">
                        {days.map((d, i) => {
                          const taken = health.meds?.[med]?.[d.str];
                          const cellColor = getBarColor(i);
                          return (
                            <div key={i} className="flex items-center justify-center" style={{ width: '30px' }}>
                              <button onClick={() => toggleMed(med, d.str)}
                                className="w-5 h-5 rounded-full flex items-center justify-center transition-all hover:scale-110"
                                style={{
                                  background: taken ? cellColor : 'transparent',
                                  border: `1.5px solid ${cellColor}`,
                                  boxShadow: taken ? `0 0 8px ${cellColor}80` : 'none',
                                }}>
                                {taken && <span className="text-[10px] font-bold" style={{ color: pickTextColor(cellColor) }}>✓</span>}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {medNames.length === 0 && (
              <p className="text-[11px] text-center mt-4 italic" style={{ color: theme.textMuted }}>
                💊 Agrega medicamentos arriba para hacer seguimiento diario
              </p>
            )}

            {/* Quick stats summary */}
            <div className="grid grid-cols-3 gap-3 mt-5 pt-4" style={{ borderTop: `1px solid ${theme.border}` }}>
              {(() => {
                const sleepVals = Object.values(health.sleep || {}).map(v => parseFloat(v) || 0).filter(v => v > 0);
                const moodVals = Object.values(health.mood || {}).map(v => parseFloat(v) || 0).filter(v => v > 0);
                const waterVals = Object.values(health.water || {}).map(v => parseFloat(v) || 0).filter(v => v > 0);
                const avg = (arr) => arr.length ? (arr.reduce((s, v) => s + v, 0) / arr.length).toFixed(1) : '—';
                return (
                  <>
                    <StatPill emoji="🌙" label="Sueño promedio" value={`${avg(sleepVals)}h`} theme={theme} />
                    <StatPill emoji="✨" label="Ánimo promedio" value={avg(moodVals)} theme={theme} />
                    <StatPill emoji="💧" label="Agua promedio" value={avg(waterVals)} theme={theme} />
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatPill = ({ emoji, label, value, theme }) => (
  <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: theme.elevated }}>
    <span className="text-2xl">{emoji}</span>
    <div>
      <p className="text-[9px] uppercase tracking-wider" style={{ color: theme.textMuted }}>{label}</p>
      <p className="text-base font-semibold" style={{ color: theme.accent }}>{value}</p>
    </div>
  </div>
);

const HealthNumRow = ({ label, emoji, unit, hint, dayList, values, onChange, theme, getColor, max = 24 }) => (
  <div className="flex items-center mb-1.5" style={{ height: '36px' }}>
    <div className="w-[200px] pl-2 flex items-center gap-2">
      <span className="text-lg">{emoji}</span>
      <div className="flex-1 min-w-0">
        <p className="text-xs truncate" style={{ color: theme.textPrimary }}>{label}</p>
        {(unit || hint) && <p className="text-[9px]" style={{ color: theme.textMuted }}>{hint || unit}</p>}
      </div>
    </div>
    <div className="flex-1 flex">
      {dayList.map((d, i) => (
        <input key={d.str} type="number" min={0} max={max}
          inputMode="numeric"
          value={values[d.str] ?? ''} onChange={(e) => onChange(d.str, e.target.value)}
          className="no-spinner text-center text-[11px] bg-transparent border-0 outline-none rounded mx-[1px] my-[3px] focus:ring-1"
          style={{
            width: '28px', height: '28px', color: theme.textPrimary,
            background: `${getColor(i)}25`, border: `1px solid ${getColor(i)}50`,
            MozAppearance: 'textfield',
          }} />
      ))}
    </div>
  </div>
);

export default Habits;
